import { Box, Text, Flex, Badge, ThemeIcon, SimpleGrid, TextInput, Stack } from "@mantine/core";
import type { CSSProperties, ReactNode } from "react";
import {
  IconShieldLock,
  IconPresentation,
  IconTestPipe,
  IconMessages,
  IconPlugConnected,
  IconPuzzle2,
  IconReportAnalytics,
  IconSchool,
  IconTool,
  IconDots,
} from "@tabler/icons-react";
import type { TablerIcon } from "@tabler/icons-react";
import {
  CUSTOMER_OFFER_OTHER_LABEL,
  CUSTOMER_OFFER_OTHER_TEXT_MAX_LENGTH,
  CUSTOMER_OFFER_PRESET_LABELS,
  type CustomerOfferHighlightApi,
  type CustomerOfferPresetLabel,
  normalizeCustomerOfferHighlightFromApi,
  parseCustomerOfferHighlightsFromApi,
  truncateCustomerOfferOtherText,
} from "@/shared/constants/customerOfferPresets";

export const WHAT_WE_OFFER_PURPLE = "#6d28d9";
/** Deep heading / icon tone */
const WHAT_WE_OFFER_INK = "#3730a3";
const WHAT_WE_OFFER_PURPLE_LIGHT = "#ede9fe";
const WHAT_WE_OFFER_SURFACE = "#f5f3ff";
const OFFER_TILE_SIZE = 58;
const OFFER_TILE_SIZE_COMPACT = 40;

const TILE_LABEL_LINE_HEIGHT = 1.25;

const CUSTOMER_OFFER_ICONS = [
  IconPresentation,
  IconTestPipe,
  IconMessages,
  IconPlugConnected,
  IconPuzzle2,
  IconReportAnalytics,
  IconSchool,
  IconTool,
  IconDots,
] as const;

export type CustomerOfferDisplayItem = { label: string; Icon: TablerIcon };

/** Icon + label for one saved offer entry. */
export function getCustomerOfferHighlightDisplayEntry(
  value: string,
): CustomerOfferDisplayItem | null {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return null;

  const { presets, otherText } = parseCustomerOfferHighlightsFromApi([trimmed]);
  const preset = presets[0] ?? "";
  const label =
    preset === CUSTOMER_OFFER_OTHER_LABEL
      ? otherText.trim() || CUSTOMER_OFFER_OTHER_LABEL
      : preset || trimmed;
  const presetIndex = preset
    ? CUSTOMER_OFFER_PRESET_LABELS.indexOf(preset as CustomerOfferPresetLabel)
    : -1;
  const Icon = presetIndex >= 0 ? CUSTOMER_OFFER_ICONS[presetIndex] : IconDots;

  return { label, Icon };
}

/** First offer for compact table cells (legacy single-icon column). */
export function getCustomerOfferHighlightDisplay(
  value: CustomerOfferHighlightApi,
): CustomerOfferDisplayItem | null {
  const values = normalizeCustomerOfferHighlightFromApi(value);
  if (values.length === 0) return null;
  return getCustomerOfferHighlightDisplayEntry(values[0]);
}

/** All offers for tooltips / multi-icon UI. */
export function getCustomerOfferHighlightDisplayList(
  value: CustomerOfferHighlightApi,
): CustomerOfferDisplayItem[] {
  return normalizeCustomerOfferHighlightFromApi(value)
    .map((entry) => getCustomerOfferHighlightDisplayEntry(entry))
    .filter((item): item is CustomerOfferDisplayItem => item != null);
}

const DEFAULT_DESCRIPTION_READ =
  "Explore the products, services, support and collaboration advantages this company offers.";

type WhatWeOfferChromeProps = {
  children: ReactNode;
  /** Yellow “New” badge (e.g. profile builder); omit on public profile. */
  showNewBadge?: boolean;
  description?: ReactNode;
};

export function WhatWeOfferChrome({ children, showNewBadge, description }: WhatWeOfferChromeProps) {
  return (
    <Box
      p={{ base: "lg", sm: "xl" }}
      style={{
        border: `2px dashed rgba(109, 40, 217, 0.42)`,
        borderRadius: 16,
        background: `linear-gradient(165deg, ${WHAT_WE_OFFER_SURFACE} 0%, ${WHAT_WE_OFFER_PURPLE_LIGHT} 42%, #fafbff 100%)`,
        boxShadow: "0 4px 24px rgba(76, 29, 149, 0.07), 0 1px 2px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Flex align="flex-start" gap={{ base: "md", sm: "lg" }} wrap="nowrap">
        <ThemeIcon
          size={52}
          radius="50%"
          variant="filled"
          style={{
            flexShrink: 0,
            backgroundColor: WHAT_WE_OFFER_PURPLE,
            boxShadow: "0 4px 14px rgba(109, 40, 217, 0.35), inset 0 1px 0 rgba(255,255,255,0.2)",
            border: "1px solid rgba(255,255,255,0.15)",
          }}
        >
          <IconShieldLock size={28} stroke={1.35} color="#ffffff" />
        </ThemeIcon>
        <Box style={{ minWidth: 0, flex: 1, paddingTop: 2 }}>
          <Flex align="center" gap="sm" wrap="wrap" rowGap={6}>
            <Text
              fw={800}
              fz={{ base: "lg", sm: 20 }}
              c={WHAT_WE_OFFER_INK}
              style={{ letterSpacing: "-0.02em", lineHeight: 1.2 }}
            >
              What We Offer
            </Text>
            {showNewBadge ? (
              <Badge
                size="sm"
                radius="xl"
                variant="filled"
                px={10}
                h={22}
                styles={{
                  root: {
                    background: "linear-gradient(180deg, #fde047 0%, #facc15 100%)",
                    color: "#1c1917",
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    fontSize: 10,
                    border: "1px solid rgba(234, 179, 8, 0.5)",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                  },
                }}
              >
                New
              </Badge>
            ) : null}
          </Flex>
          <Text
            fz="sm"
            c="#64748b"
            lh={1.55}
            mt={10}
            maw={720}
            style={{ fontWeight: 400 }}
          >
            {description ?? DEFAULT_DESCRIPTION_READ}
          </Text>
        </Box>
      </Flex>

      <Box
        mt={{ base: "lg", sm: "xl" }}
        pt={{ base: "lg", sm: "xl" }}
        style={{
          borderTop: "1px solid rgba(109, 40, 217, 0.12)",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

type WhatWeOfferIconTilesProps = {
  selectedLabels: string[];
  /** When true, non-selected tiles are dimmed (read-only profile). */
  dimUnselected?: boolean;
  /** Toggle preset on click (profile builder). Omit for read-only. */
  onToggleLabel?: (label: string) => void;
  /** Smaller tiles for company header cards. */
  size?: "default" | "compact";
  /** Even 3-column grid, or responsive: grid on mobile + single row on desktop. */
  layout?: "flex" | "grid" | "responsive";
  /** Tile row alignment when using flex layout. */
  justify?: "flex-start" | "center" | "space-between";
  /** Tighter spacing between tiles (e.g. discover banner). */
  dense?: boolean;
  /** Bold labels under every icon. */
  boldLabels?: boolean;
  /** Show only selected preset tiles (read-only card). */
  selectedOnly?: boolean;
  /** Custom text when “Other” is selected (shown below the icon instead of the “Other” label). */
  otherText?: string;
  /** Inline editor below the Other icon (discover banner). */
  onOtherTextChange?: (text: string) => void;
  /** Compact profile card: space-between row, full labels (no ellipsis). */
  profileEmbed?: boolean;
  /** Horizontal gap between tiles (px); overrides compact defaults when set. */
  tileGapPx?: number;
};

export function WhatWeOfferIconTiles({
  selectedLabels,
  dimUnselected = false,
  onToggleLabel,
  size = "default",
  layout = "flex",
  justify = "center",
  dense = false,
  boldLabels = false,
  selectedOnly = false,
  otherText = "",
  onOtherTextChange,
  profileEmbed = false,
  tileGapPx,
}: WhatWeOfferIconTilesProps) {
  const selectedSet = new Set(selectedLabels);
  const visibleLabels = selectedOnly
    ? CUSTOMER_OFFER_PRESET_LABELS.filter((label) => selectedSet.has(label))
    : CUSTOMER_OFFER_PRESET_LABELS;

  if (selectedOnly && visibleLabels.length === 0) {
    return null;
  }
  const isCompact = size === "compact";
  const isProfileEmbed = profileEmbed && isCompact;
  const tileSize = isCompact ? (isProfileEmbed ? 36 : OFFER_TILE_SIZE_COMPACT) : OFFER_TILE_SIZE;
  const tileWidth = isCompact ? (isProfileEmbed ? 88 : 80) : 104;
  const iconSizeSelected = isCompact ? (isProfileEmbed ? 16 : 18) : 27;
  const iconSizeDefault = isCompact ? (isProfileEmbed ? 14 : 16) : 25;
  const labelFontSize = isProfileEmbed ? 10 : isCompact ? (dense ? 12 : 11) : 10;

  const isGridCell = layout === "grid" || layout === "responsive";
  const labelMaxWidth = isGridCell || isProfileEmbed ? "100%" : tileWidth;
  const presetLabelStyle: CSSProperties = isProfileEmbed
    ? {
        width: "100%",
        maxWidth: "100%",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        lineHeight: TILE_LABEL_LINE_HEIGHT,
        overflow: "visible",
      }
    : {
        maxWidth: labelMaxWidth,
        width: labelMaxWidth,
      };
  const iconLabelGap = isProfileEmbed ? 4 : dense ? (isCompact ? 4 : 6) : isCompact ? 6 : 10;
  const gridSpacing = dense ? 2 : 4;
  const gridVerticalSpacing = dense ? 4 : 6;

  const renderIconCircle = (index: number, selected: boolean) => {
    const IconCmp = CUSTOMER_OFFER_ICONS[index] ?? IconDots;
    const dim = dimUnselected && !selected;
    return (
      <Flex
        align="center"
        justify="center"
        style={{
          flexShrink: 0,
          width: tileSize,
          height: tileSize,
          borderRadius: "50%",
          border: selected
            ? `2px solid ${WHAT_WE_OFFER_PURPLE}`
            : isCompact
              ? "1px solid rgba(196, 181, 253, 0.45)"
              : "1.5px solid rgba(196, 181, 253, 0.95)",
          backgroundColor: selected ? "#ffffff" : "rgba(255,255,255,0.92)",
          boxShadow: selected
            ? isCompact
              ? `0 0 0 2px ${WHAT_WE_OFFER_PURPLE_LIGHT}`
              : `0 0 0 4px ${WHAT_WE_OFFER_PURPLE_LIGHT}, 0 6px 16px rgba(109, 40, 217, 0.12)`
            : isCompact
              ? "none"
              : "0 2px 8px rgba(15, 23, 42, 0.04)",
          opacity: dim ? 0.38 : 1,
          transition: "box-shadow 0.2s ease, border-color 0.2s ease, opacity 0.2s ease",
        }}
      >
        <IconCmp
          size={selected ? iconSizeSelected : iconSizeDefault}
          color={dim ? "#a78bfa" : WHAT_WE_OFFER_PURPLE}
          stroke={selected ? 1.5 : 1.35}
        />
      </Flex>
    );
  };

  const tileInner = (
    label: string,
    index: number,
    selected: boolean,
    customLabelBelow?: ReactNode,
  ) => {
    const dim = dimUnselected && !selected;
    const interactive = Boolean(onToggleLabel);
    const isOtherSelected = label === CUSTOMER_OFFER_OTHER_LABEL && selected;
    const showCustomBelow = isOtherSelected && (customLabelBelow != null || onOtherTextChange);

    return (
      <Flex
        direction="column"
        align="center"
        gap={iconLabelGap}
        onClick={interactive ? () => onToggleLabel?.(label) : undefined}
        style={interactive ? { cursor: "pointer" } : undefined}
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        onKeyDown={
          interactive
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggleLabel?.(label);
                }
              }
            : undefined
        }
      >
        {renderIconCircle(index, selected)}
        {showCustomBelow ? (
          customLabelBelow ?? null
        ) : (
          <Text
            ta="center"
            fz={labelFontSize}
            fw={boldLabels ? 700 : selected ? 700 : 600}
            c={selected ? WHAT_WE_OFFER_INK : dim ? "#94a3b8" : "#475569"}
            style={{
              opacity: dim ? 0.5 : 1,
              ...presetLabelStyle,
            }}
          >
            {label}
          </Text>
        )}
      </Flex>
    );
  };

  const renderOtherCustomLabel = (selected: boolean) => {
    if (!selected) return null;
    const trimmed = otherText.trim();
    const labelTypography = {
      fz: labelFontSize,
      fw: boldLabels ? 700 : 700,
      c: WHAT_WE_OFFER_INK,
      maw: labelMaxWidth,
    };

    if (onOtherTextChange) {
      return (
        <TextInput
          size="xs"
          placeholder="Your offer"
          maxLength={CUSTOMER_OFFER_OTHER_TEXT_MAX_LENGTH}
          value={otherText}
          onChange={(e) => onOtherTextChange(truncateCustomerOfferOtherText(e.currentTarget.value))}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          styles={{
            root: { width: labelMaxWidth, maxWidth: "100%" },
            input: {
              textAlign: "center",
              borderColor: "rgba(180, 83, 9, 0.35)",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              height: isCompact ? 28 : 32,
              fontSize: labelTypography.fz,
              fontWeight: labelTypography.fw,
            },
          }}
          aria-label="Describe your offer"
        />
      );
    }
    if (!trimmed) return null;
    return (
      <Text
        ta="center"
        {...labelTypography}
        style={{
          width: "100%",
          maxWidth: "100%",
          lineHeight: TILE_LABEL_LINE_HEIGHT,
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          overflow: "visible",
        }}
      >
        {trimmed}
      </Text>
    );
  };

  const renderTile = (
    label: string,
    index: number,
    boxStyle: CSSProperties,
  ) => {
    const selected = selectedSet.has(label);
    const isOther = label === CUSTOMER_OFFER_OTHER_LABEL;
    const otherCustomLabel = isOther ? renderOtherCustomLabel(selected) : undefined;

    const otherBoxStyle: CSSProperties =
      isOther && selected && (otherCustomLabel || onOtherTextChange)
        ? (() => {
            const { width: _w, maxWidth: _maxW, flex: _flex, ...rest } = boxStyle;
            return {
              ...rest,
              width: "auto",
              maxWidth: isProfileEmbed ? "100%" : isCompact ? 120 : 140,
              flex: "0 0 auto",
              flexShrink: 0,
            };
          })()
        : boxStyle;

    return (
      <Box key={label} style={otherBoxStyle}>
        {tileInner(label, index, selected, otherCustomLabel ?? undefined)}
      </Box>
    );
  };

  if (layout === "responsive") {
    return (
      <>
        <Box hiddenFrom="sm">
          <SimpleGrid cols={3} spacing={gridSpacing} verticalSpacing={gridVerticalSpacing}>
            {visibleLabels.map((label) => {
              const index = CUSTOMER_OFFER_PRESET_LABELS.indexOf(label);
              return renderTile(label, index, {
                width: "100%",
                minHeight: isCompact ? 80 : undefined,
              });
            })}
          </SimpleGrid>
        </Box>
        <Flex
          visibleFrom="sm"
          gap={dense ? 4 : 6}
          wrap="nowrap"
          justify="space-between"
          align="flex-start"
          w="100%"
          style={{ overflowX: "auto" }}
        >
          {visibleLabels.map((label) => {
            const index = CUSTOMER_OFFER_PRESET_LABELS.indexOf(label);
            const count = visibleLabels.length;
            return renderTile(label, index, {
              flex: "1 1 0",
              minWidth: 0,
              maxWidth: count > 0 ? `${100 / count}%` : undefined,
            });
          })}
        </Flex>
      </>
    );
  }

  const tiles = visibleLabels.map((label) => {
    const index = CUSTOMER_OFFER_PRESET_LABELS.indexOf(label);
    if (isProfileEmbed) {
      return renderTile(label, index, {
        flex: "0 0 auto",
        width: tileWidth,
        minWidth: tileWidth,
        maxWidth: tileWidth,
      });
    }
    return renderTile(label, index, {
      width: layout === "grid" ? "100%" : tileWidth,
      flex: layout === "grid" ? undefined : "0 0 auto",
      minHeight: layout === "grid" && isCompact ? 84 : undefined,
    });
  });

  if (layout === "grid") {
    return (
      <SimpleGrid cols={3} spacing={4} verticalSpacing={6}>
        {tiles}
      </SimpleGrid>
    );
  }

  const flexJustify = isProfileEmbed
    ? "flex-start"
    : justify === "space-between"
      ? "space-between"
      : justify === "flex-start"
        ? "flex-start"
        : isCompact
          ? { base: "flex-start" as const, sm: "flex-start" as const }
          : { base: "center" as const, sm: "flex-start" as const };

  const flexGap =
    tileGapPx != null
      ? tileGapPx
      : isProfileEmbed
        ? 10
        : isCompact
          ? dense
            ? { base: 8, sm: 10 }
            : { base: 16, sm: 20 }
          : { base: 12, sm: 16 };

  return (
    <Flex
      gap={flexGap}
      wrap="wrap"
      justify={flexJustify}
      align="flex-start"
      w={isProfileEmbed ? "100%" : undefined}
      rowGap={
        isProfileEmbed ? 8 : dense ? (isCompact ? 8 : 10) : isCompact ? { base: 10, sm: 12 } : { base: 16, sm: 18 }
      }
    >
      {tiles}
    </Flex>
  );
}

const embedHeader = (
  <Flex align="center" gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
    <ThemeIcon
      size={24}
      radius="50%"
      variant="filled"
      style={{
        flexShrink: 0,
        backgroundColor: WHAT_WE_OFFER_PURPLE,
        boxShadow: "0 2px 8px rgba(109, 40, 217, 0.25)",
      }}
    >
      <IconShieldLock size={14} stroke={1.35} color="#ffffff" />
    </ThemeIcon>
    <Text fw={700} fz="xs" c={WHAT_WE_OFFER_INK} style={{ letterSpacing: "-0.01em", lineHeight: 1.2 }}>
      What We Offer
    </Text>
  </Flex>
);

/** Compact “What We Offer” block for company header cards (anonymous + matched views). */
export function CustomerOfferHighlightEmbed({ value }: { value: CustomerOfferHighlightApi }) {
  const { presets, otherText } = parseCustomerOfferHighlightsFromApi(value);
  if (presets.length === 0 && !otherText.trim()) return null;

  const tileProps = {
    selectedLabels: presets,
    selectedOnly: true as const,
    size: "compact" as const,
    justify: "flex-start" as const,
    boldLabels: true,
    otherText,
  };

  return (
    <Box w="100%" miw={0} style={{ maxWidth: "100%" }}>
      {/* Mobile: header on top, tiles in a 3-column grid (avoids crushed flex beside title). */}
      <Box hiddenFrom="sm">
        <Stack gap="sm">
          {embedHeader}
          <WhatWeOfferIconTiles {...tileProps} layout="grid" dense />
        </Stack>
      </Box>

      {/* Desktop: title left, offer tiles from the start of the remaining row. */}
      <Flex visibleFrom="sm" align="flex-start" gap={14} wrap="nowrap" w="100%">
        <Box pt={2}>{embedHeader}</Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <WhatWeOfferIconTiles {...tileProps} layout="flex" tileGapPx={16} />
        </Box>
      </Flex>
    </Box>
  );
}

/** Read-only “What We Offer” for profile / discover views. */
export function CustomerOfferHighlightReadOnly({
  value,
  showNewBadge = false,
}: {
  value: CustomerOfferHighlightApi;
  showNewBadge?: boolean;
}) {
  const { presets, otherText } = parseCustomerOfferHighlightsFromApi(value);
  if (presets.length === 0 && !otherText.trim()) return null;

  return (
    <Box mb={{ base: "lg", sm: "xl" }}>
      <WhatWeOfferChrome showNewBadge={showNewBadge}>
        <WhatWeOfferIconTiles
          selectedLabels={presets}
          dimUnselected
          otherText={otherText}
        />
      </WhatWeOfferChrome>
    </Box>
  );
}
